// Feats that change a move at the table (qa-runs#366; docs/MRCS/feats.md §10b). The feats, their weapons and their limits
// are the system's data (`features.combat.move_feats`): this file only reads them against what the table knows.
//
// A move-attack: a Move stays open through a single attack with the feat's weapon, so what is left of it can be walked
// after. Nothing new is spent: the Move took its move action and the attack its standard one. A follow: a foe's 5-foot
// step away offers each adjacent carrier a step into the square it left; offered, never made.
import { cellOf } from '../grid/cells.js';
import { conditionKey } from './chainTable.js';
import { HELPLESS_CONDITIONS, hostile } from './triggers.js';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isKey = (v) => typeof v === 'string' && /^[a-z][a-z0-9_]{0,40}$/.test(v);
const isText = (v, most = 60) => typeof v === 'string' && v.trim().length > 0 && v.length <= most;
const only = (o, keys) => Object.keys(o).every((k) => keys.includes(k));
function parseAttack(v) {
    if (!isObj(v) || !only(v, ['key', 'feat', 'label', 'weapon', 'before', 'no_heavy', 'spares_target', 'not_start_adjacent']))
        return null;
    if (!isKey(v.key) || !isText(v.feat) || !isText(v.label) || (v.weapon !== 'melee' && v.weapon !== 'ranged'))
        return null;
    if (v.before !== undefined && !(typeof v.before === 'number' && v.before > 0 && v.before <= 1000))
        return null;
    for (const k of ['no_heavy', 'spares_target', 'not_start_adjacent'])
        if (v[k] !== undefined && v[k] !== true)
            return null;
    return {
        key: v.key, feat: v.feat.trim(), label: v.label.trim(), weapon: v.weapon, ...(v.before !== undefined ? { before: v.before } : {}),
        ...(v.no_heavy ? { noHeavy: true } : {}), ...(v.spares_target ? { sparesTarget: true } : {}), ...(v.not_start_adjacent ? { notStartAdjacent: true } : {}),
    };
}
function parseFollow(v) {
    if (!isObj(v) || !only(v, ['feat', 'label', 'also']) || !isText(v.feat) || !isText(v.label))
        return null;
    const also = [];
    for (const a of Array.isArray(v.also) ? v.also : []) {
        if (!isObj(a) || !only(a, ['feat', 'free', 'report']) || !isText(a.feat) || (a.free !== undefined && a.free !== true) || (a.report !== undefined && !isText(a.report, 200)))
            return null;
        also.push({ feat: a.feat.trim(), ...(a.free ? { free: true } : {}), ...(typeof a.report === 'string' ? { report: a.report.trim() } : {}) });
    }
    return { feat: v.feat.trim(), label: v.label.trim(), also };
}
/** `features.combat.move_feats`, strictly: null when any part is malformed, so the table plays none. */
export function parseMoveFeats(v) {
    if (!isObj(v) || !only(v, ['attacks', 'follow']) || !Array.isArray(v.attacks) || v.attacks.length > 20)
        return null;
    const attacks = v.attacks.map(parseAttack);
    if (!attacks.every((a) => a !== null) || new Set(attacks.map((a) => a.key)).size !== attacks.length)
        return null;
    const follow = v.follow === undefined ? undefined : parseFollow(v.follow);
    if (follow === null)
        return null;
    return { attacks, ...(follow ? { follow } : {}) };
}
/** The names an entry is carried by, as a stat block's and a sheet's feats are written. */
export const featKeys = (name) => { const k = conditionKey(name); return [k, k.replace(/-/g, '_')]; };
/** The move-attack a carrier may use for this attack, the first the system lists. */
export function moveAttackFor(rules, ranged, has) {
    return rules?.attacks.find((a) => (a.weapon === 'ranged') === ranged && has(a.feat)) ?? null;
}
/** Why this attack ends the move all the same, in words; null when the move stays open. */
export function moveAttackRefusal(m, f) {
    if (m.noHeavy && f.heavy)
        return `${m.label}: not in heavy armor, so the attack ends the move.`;
    if (m.before !== undefined && f.movedSquares * f.perSquare < m.before - 1e-9)
        return `${m.label}: it needs ${m.before} ${f.unit} of movement before the attack, so the attack ends the move.`;
    if (m.notStartAdjacent && f.startAdjacent)
        return `${m.label}: not at a foe next to you when your turn started, so the attack ends the move.`;
    return null;
}
/** Whether two points are the same square or next to each other. */
export function adjacentCells(scene, extent, a, b) {
    const x = cellOf(scene.grid, extent, a);
    const y = cellOf(scene.grid, extent, b);
    return Math.max(Math.abs(x.i - y.i), Math.abs(x.j - y.j)) <= 1;
}
/** Who may follow a step: each foe of the mover, able to act, next to where it stood and no longer next to it. */
export function stepFollowers(scene, moverId, from, extent) {
    const mover = scene.objects[moverId];
    if (!mover || mover.kind !== 'token')
        return [];
    return Object.values(scene.objects).filter((t) => t.id !== moverId && t.kind === 'token' && !t.dead && hostile(t, mover)
        && !(t.values.conditions ?? []).some((c) => HELPLESS_CONDITIONS.includes(conditionKey(c)))
        && adjacentCells(scene, extent, t, from) && !adjacentCells(scene, extent, t, mover)).map((t) => t.id);
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function parseStepFollow(v) {
    if (!isObj(v) || !only(v, ['kind', 'token', 'foe']) || v.kind !== 'step.follow' || typeof v.token !== 'string' || !UUID.test(v.token) || typeof v.foe !== 'string' || !UUID.test(v.foe))
        return null;
    return { kind: 'step.follow', token: v.token, foe: v.foe };
}
//# sourceMappingURL=moveFeats.js.map